import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog/Dialog';
import { Spinner } from '@/components/ui/Spinner/Spinner';
import { SchedulingPausedStep } from '@/features/scheduling/components/SchedulingPaused/SchedulingPausedStep';
import { useIsSchedulingBlocked } from '@/features/systemBanner/hooks/useIsSchedulingBlocked';
import { useErrorTranslation } from '@/hooks/useErrorTranslation';
import { DialogDescription } from '@radix-ui/react-dialog';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useRescheduleFlow } from './context/useRescheduleFlow';
import { DateAndTimeStep } from './steps/DateAndTimeStep/DateAndTimeStep';
import { ErrorStep } from './steps/ErrorStep/ErrorStep';
import { LateRescheduleStep } from './steps/LateRescheduleStep/LateRescheduleStep';
import { SuccessStep } from './steps/SuccessStep/SuccessStep';
import { SummaryStep } from './steps/SummaryStep/SummaryStep';

const RescheduleDialogContent = () => {
  const { t } = useTranslation('scheduling', { keyPrefix: 'rescheduleDialog' });
  const { translateError } = useErrorTranslation();

  const { currentStep, resetFlow, isRescheduleDialogOpen, closeRescheduleDialog, data } = useRescheduleFlow();
  const isSchedulingBlocked = useIsSchedulingBlocked();

  useEffect(() => {
    if (!isRescheduleDialogOpen) {
      resetFlow();
    }
  }, [isRescheduleDialogOpen, resetFlow]);

  const dialogContainer = useRef<HTMLDivElement | null>(null);

  const getContent = () => {
    if (isSchedulingBlocked && currentStep !== 'success') {
      return <SchedulingPausedStep onClose={closeRescheduleDialog} />;
    }

    if (data.config.isLoading) {
      return (
        <div className='flex flex-1 items-center justify-center p-6' role='status' aria-live='polite'>
          <Spinner className='size-20 stroke-1' />
        </div>
      );
    }

    if (data.config.error) {
      return (
        <div className='flex flex-1 items-center justify-center p-6' role='alert' aria-live='assertive'>
          <p className='text-text-default-secondary'>{translateError(data.config.error)}</p>
        </div>
      );
    }

    switch (currentStep) {
      case 'selectNewDate': {
        return <DateAndTimeStep portalContainerRef={dialogContainer} />;
      }
      case 'summary': {
        return <SummaryStep />;
      }
      case 'success': {
        return <SuccessStep />;
      }
      case 'lateReschedule': {
        return <LateRescheduleStep />;
      }
      case 'error': {
        return <ErrorStep />;
      }
    }
  };

  return (
    <DialogContent ref={dialogContainer} className='desktop:!max-h-[840px]'>
      <DialogHeader>
        <DialogTitle>{t('title')}</DialogTitle>
      </DialogHeader>
      <DialogDescription className='sr-only'>{t('description')}</DialogDescription>
      {getContent()}
    </DialogContent>
  );
};

export const RescheduleDialog = () => {
  const { isRescheduleDialogOpen, closeRescheduleDialog } = useRescheduleFlow();

  return (
    <Dialog open={isRescheduleDialogOpen} onOpenChange={closeRescheduleDialog}>
      <RescheduleDialogContent />
    </Dialog>
  );
};
